import React, { useState, useEffect } from 'react';
import { useAppDialogs } from '../context/AppDialogContext';
import { 
  Briefcase, Users, FileText, Settings, Key, BarChart3, Database, 
  Plus, Trash2, ShieldAlert, CheckCircle, RefreshCw, Send, AlertTriangle, Info, Search 
} from 'lucide-react';

interface OrganizationManagerProps {
  currentUser: any;
  toast: {
    success: (msg: string) => void;
    error: (msg: string) => void;
    warning: (msg: string) => void;
    info: (msg: string) => void;
  };
}

export const OrganizationManager: React.FC<OrganizationManagerProps> = ({ currentUser, toast }) => {
  const { confirmAction } = useAppDialogs();
  const [activeSubTab, setActiveSubTab] = useState<'blueprints' | 'catalogs' | 'events_rules' | 'api_keys' | 'reports' | 'members' | 'global_control'>('blueprints');
  const [loading, setLoading] = useState(false);
  const [orgId, setOrgId] = useState<number>(currentUser?.default_organization_id || 1);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [hasMemberMgmtAccess, setHasMemberMgmtAccess] = useState(true);

  // Catalog states
  const [areas, setAreas] = useState<any[]>([]);
  const [responsibles, setResponsibles] = useState<any[]>([]);
  const [docTypes, setDocTypes] = useState<any[]>([]);
  const [docStatuses, setDocStatuses] = useState<any[]>([]);
  
  // Event & rules states
  const [eventSchemas, setEventSchemas] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [rules, setRules] = useState<any[]>([]);

  // API Key & Event Logs states
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [eventLogs, setEventLogs] = useState<any[]>([]);

  // Members & Org creation state
  const [members, setMembers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [newOrg, setNewOrg] = useState({
    name: '',
    slug: '',
    type: 'corporate',
    ruc: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    status: 'active'
  });
  const [newMember, setNewMember] = useState({
    userId: '',
    role: 'member'
  });

  // Search and Edit States
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [eventsRulesSearchQuery, setEventsRulesSearchQuery] = useState('');
  const [orgSearchQuery, setOrgSearchQuery] = useState('');

  const [editingArea, setEditingArea] = useState<any | null>(null);
  const [editingResponsible, setEditingResponsible] = useState<any | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<any | null>(null);
  const [editingRule, setEditingRule] = useState<any | null>(null);
  const [editingOrg, setEditingOrg] = useState<any | null>(null);
  const [editingMember, setEditingMember] = useState<any | null>(null);

  const [apiKeySearchQuery, setApiKeySearchQuery] = useState('');
  const [reportsSearchQuery, setReportsSearchQuery] = useState('');
  const [reportMetrics, setReportMetrics] = useState<any>({
    total_messages: 0,
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    provider_meta: 0,
    provider_whatsmeow: 0,
    estimated_cost_usd: 0
  });
  const [reportLogs, setReportLogs] = useState<any[]>([]);

  // Forms
  const [newArea, setNewArea] = useState({ name: '', code: '', description: '' });
  const [newResponsible, setNewResponsible] = useState({ fullName: '', position: '', whatsappPhone: '', email: '', areaId: '' });
  const [newDocType, setNewDocType] = useState({ name: '', code: '', description: '' });
  const [newDocStatus, setNewDocStatus] = useState({ name: '', code: '', notify: true, priority: 'normal' });
  const [newApiKey, setNewApiKey] = useState({ name: '', expiresDays: 30 });
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);

  const fetchOrgs = async (selectDefault = false) => {
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch('/api/organizations', { headers }).then(r => r.json());
      if (res.success) {
        setOrganizations(res.organizations || []);
        if (selectDefault && res.organizations && res.organizations.length > 0) {
          const gorehco = res.organizations.find((o: any) => o.slug === 'gorehco');
          if (gorehco) {
            setOrgId(gorehco.id);
          } else {
            setOrgId(res.organizations[0].id);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching organizations", err);
    }
  };

  // Fetch organizations list on mount
  useEffect(() => {
    fetchOrgs(true);
  }, [currentUser]);

  // Fetch all catalogs and rules when sub-tab changes
  useEffect(() => {
    if (activeSubTab === 'catalogs') {
      fetchCatalogs();
    } else if (activeSubTab === 'events_rules') {
      fetchEventsAndRules();
    } else if (activeSubTab === 'api_keys') {
      fetchApiKeysAndLogs();
    } else if (activeSubTab === 'members') {
      fetchMembersAndUsers();
    } else if (activeSubTab === 'global_control') {
      fetchMembersAndUsers();
    } else if (activeSubTab === 'reports') {
      fetchReports();
    }
  }, [activeSubTab, orgId]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const [metricsRes, logsRes] = await Promise.all([
        fetch(`/api/org/reports/consumption?org_id=${orgId}`, { headers }).then(r => r.json()),
        fetch(`/api/org/reports/notifications?org_id=${orgId}`, { headers }).then(r => r.json())
      ]);
      if (metricsRes.success && metricsRes.metrics) {
        setReportMetrics(metricsRes.metrics);
      }
      if (logsRes.success && logsRes.logs) {
        setReportLogs(logsRes.logs);
      }
    } catch (e) {
      toast.error("Error al cargar reportes");
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogs = async () => {
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const [areasRes, respRes, typesRes, statusRes] = await Promise.all([
        fetch(`/api/org/areas?org_id=${orgId}`, { headers }).then(r => r.json()),
        fetch(`/api/org/responsibles?org_id=${orgId}`, { headers }).then(r => r.json()),
        fetch(`/api/org/document-types?org_id=${orgId}`, { headers }).then(r => r.json()),
        fetch(`/api/org/document-statuses?org_id=${orgId}`, { headers }).then(r => r.json())
      ]);

      if (areasRes.success) setAreas(areasRes.areas || []);
      if (respRes.success) setResponsibles(respRes.responsibles || []);
      if (typesRes.success) setDocTypes(typesRes.types || []);
      if (statusRes.success) setDocStatuses(statusRes.statuses || []);
    } catch (e) {
      toast.error("Error al cargar los catálogos");
    } finally {
      setLoading(false);
    }
  };

  const fetchEventsAndRules = async () => {
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const [schemasRes, templatesRes, rulesRes] = await Promise.all([
        fetch(`/api/org/event-schemas?org_id=${orgId}`, { headers }).then(r => r.json()),
        fetch(`/api/org/notification-templates?org_id=${orgId}`, { headers }).then(r => r.json()),
        fetch(`/api/org/notification-rules?org_id=${orgId}`, { headers }).then(r => r.json())
      ]);

      if (schemasRes.success) setEventSchemas(schemasRes.schemas || []);
      if (templatesRes.success) setTemplates(templatesRes.templates || []);
      if (rulesRes.success) setRules(rulesRes.rules || []);
    } catch (e) {
      toast.error("Error al cargar eventos y reglas");
    } finally {
      setLoading(false);
    }
  };

  const fetchApiKeysAndLogs = async () => {
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const keysRes = await fetch(`/api/org/api-keys?org_id=${orgId}`, { headers }).then(r => r.json());
      if (keysRes.success) setApiKeys(keysRes.keys || []);
    } catch (e) {
      toast.error("Error al cargar API Keys");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembersAndUsers = async () => {
    setLoading(true);
    setHasMemberMgmtAccess(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const [membersRes, usersRes] = await Promise.all([
        fetch(`/api/org/members?org_id=${orgId}`, { headers }).then(async r => {
          if (r.status === 403) {
            setHasMemberMgmtAccess(false);
            return { success: false };
          }
          return r.json();
        }),
        fetch('/api/users', { headers }).then(r => r.json())
      ]);

      if (membersRes.success) {
        setMembers(membersRes.members || []);
      }
      if (usersRes.success) {
        setAllUsers(usersRes.users || []);
      }
    } catch (e) {
      toast.error("Error al cargar datos de miembros");
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrg.name || !newOrg.slug) {
      toast.error("Nombre y Slug son requeridos");
      return;
    }
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch('/api/organizations', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newOrg.name,
          slug: newOrg.slug,
          type: newOrg.type,
          ruc: newOrg.ruc ? newOrg.ruc : null,
          contact_name: newOrg.contactName ? newOrg.contactName : null,
          contact_email: newOrg.contactEmail ? newOrg.contactEmail : null,
          contact_phone: newOrg.contactPhone ? newOrg.contactPhone : null,
          status: newOrg.status
        })
      }).then(r => r.json());

      if (res.success) {
        toast.success("Organización creada exitosamente");
        setNewOrg({
          name: '',
          slug: '',
          type: 'corporate',
          ruc: '',
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          status: 'active'
        });
        await fetchOrgs();
      } else {
        toast.error(res.message || "Error al crear organización");
      }
    } catch (err) {
      toast.error("Error al crear organización");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMember.userId) {
      toast.error("Por favor selecciona un usuario");
      return;
    }
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/members?org_id=${orgId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          user_id: parseInt(newMember.userId, 10),
          role: newMember.role
        })
      }).then(r => r.json());

      if (res.success) {
        toast.success("Usuario asignado exitosamente");
        setNewMember({ userId: '', role: 'member' });
        await fetchMembersAndUsers();
      } else {
        toast.error(res.message || "Error al asignar usuario");
      }
    } catch (err) {
      toast.error("Error al asignar usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (id: number) => {
    if (!(await confirmAction({ title: 'Revocar acceso', message: '¿Estás seguro de quitar el acceso a este usuario para esta organización?', type: 'danger' }))) return;
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/members/${id}?org_id=${orgId}`, {
        method: 'DELETE',
        headers
      }).then(r => r.json());

      if (res.success) {
        toast.success("Acceso revocado exitosamente");
        await fetchMembersAndUsers();
      } else {
        toast.error(res.message || "Error al revocar acceso");
      }
    } catch (err) {
      toast.error("Error al revocar acceso");
    } finally {
      setLoading(false);
    }
  };

  const applyBlueprint = async (code: string) => {
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/organizations/${orgId}/apply-blueprint`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ blueprint_code: code })
      }).then(r => r.json());

      if (res.success) {
        toast.success(`Blueprint '${code}' aplicado con éxito`);
      } else {
        toast.error(res.message || "Error al aplicar blueprint");
      }
    } catch (e) {
      toast.error("Error de red al aplicar blueprint");
    } finally {
      setLoading(false);
    }
  };

  // Add Item actions
  const handleAddArea = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newArea.name) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/areas?org_id=${orgId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...newArea, organization_id: orgId })
      }).then(r => r.json());

      if (res.success) {
        toast.success("Área creada con éxito");
        setNewArea({ name: '', code: '', description: '' });
        fetchCatalogs();
      }
    } catch (e) {
      toast.error("Error al crear área");
    }
  };

  const handleAddResponsible = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newResponsible.fullName || !newResponsible.whatsappPhone) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const areaIdNum = newResponsible.areaId ? parseInt(newResponsible.areaId, 10) : null;
      const res = await fetch(`/api/org/responsibles?org_id=${orgId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          full_name: newResponsible.fullName,
          position: newResponsible.position || null,
          whatsapp_phone: newResponsible.whatsappPhone,
          email: newResponsible.email || null,
          area_id: areaIdNum,
          organization_id: orgId
        })
      }).then(r => r.json());

      if (res.success) {
        toast.success("Responsable creado con éxito");
        setNewResponsible({ fullName: '', position: '', whatsappPhone: '', email: '', areaId: '' });
        fetchCatalogs();
      }
    } catch (e) {
      toast.error("Error al crear responsable");
    }
  };

  const handleGenerateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newApiKey.name) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/api-keys?org_id=${orgId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newApiKey.name,
          expires_in_days: newApiKey.expiresDays,
          rate_limit_per_minute: 60
        })
      }).then(r => r.json());

      if (res.success) {
        toast.success("API Key generada correctamente");
        setGeneratedKey(res.apiKey);
        setNewApiKey({ name: '', expiresDays: 30 });
        fetchApiKeysAndLogs();
      }
    } catch (e) {
      toast.error("Error al generar API Key");
    }
  };

  const handleUpdateArea = async (id: number, name: string, code: string, description: string) => {
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/areas/${id}?org_id=${orgId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ name, code, description })
      }).then(r => r.json());
      if (res.success) {
        toast.success("Área actualizada correctamente");
        setEditingArea(null);
        fetchCatalogs();
      } else {
        toast.error(res.message || "Error al actualizar área");
      }
    } catch (e) {
      toast.error("Error al actualizar área");
    }
  };

  const handleUpdateResponsible = async (id: number, fullName: string, position: string, whatsappPhone: string, email: string, areaId: any) => {
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const areaIdNum = areaId ? parseInt(areaId, 10) : null;
      const res = await fetch(`/api/org/responsibles/${id}?org_id=${orgId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          full_name: fullName,
          position: position || null,
          whatsapp_phone: whatsappPhone,
          email: email || null,
          area_id: areaIdNum
        })
      }).then(r => r.json());
      if (res.success) {
        toast.success("Responsable actualizado correctamente");
        setEditingResponsible(null);
        fetchCatalogs();
      } else {
        toast.error(res.message || "Error al actualizar responsable");
      }
    } catch (e) {
      toast.error("Error al actualizar responsable");
    }
  };

  const handleUpdateOrg = async (id: number, name: string, type: string, ruc: string, contactName: string, contactEmail: string, contactPhone: string, status: string) => {
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/organizations/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          name,
          type,
          ruc: ruc || null,
          contact_name: contactName || null,
          contact_email: contactEmail || null,
          contact_phone: contactPhone || null,
          status
        })
      }).then(r => r.json());
      if (res.success) {
        toast.success("Organización actualizada correctamente");
        setEditingOrg(null);
        fetchOrgs();
      } else {
        toast.error(res.message || "Error al actualizar organización");
      }
    } catch (e) {
      toast.error("Error al actualizar organización");
    }
  };

  const handleDeleteOrg = async (id: number) => {
    if (!(await confirmAction({ title: 'Eliminar organización', message: '¿Estás seguro de eliminar esta organización de manera permanente? Esta acción no se puede deshacer.', type: 'danger', confirmText: 'Eliminar' }))) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/organizations/${id}`, {
        method: 'DELETE',
        headers
      }).then(r => r.json());
      if (res.success) {
        toast.success("Organización eliminada correctamente");
        fetchOrgs();
      } else {
        toast.error(res.message || "Error al eliminar organización");
      }
    } catch (e) {
      toast.error("Error al eliminar organización");
    }
  };

  const handleUpdateTemplate = async (id: number, name: string, code: string, body: string) => {
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/notification-templates/${id}?org_id=${orgId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ name, code, body })
      }).then(r => r.json());
      if (res.success) {
        toast.success("Plantilla actualizada correctamente");
        setEditingTemplate(null);
        fetchEventsAndRules();
      } else {
        toast.error(res.message || "Error al actualizar plantilla");
      }
    } catch (e) {
      toast.error("Error al actualizar plantilla");
    }
  };

  const handleUpdateRule = async (id: number, name: string, eventType: string, recipientMode: string) => {
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/notification-rules/${id}?org_id=${orgId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ name, event_type: eventType, recipient_mode: recipientMode })
      }).then(r => r.json());
      if (res.success) {
        toast.success("Regla de enrutamiento actualizada");
        setEditingRule(null);
        fetchEventsAndRules();
      } else {
        toast.error(res.message || "Error al actualizar regla");
      }
    } catch (e) {
      toast.error("Error al actualizar regla");
    }
  };

  const handleUpdateMember = async (id: number, role: string) => {
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/members/${id}?org_id=${orgId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ role })
      }).then(r => r.json());
      if (res.success) {
        toast.success("Rol de miembro actualizado correctamente");
        setEditingMember(null);
        fetchMembersAndUsers();
      } else {
        toast.error(res.message || "Error al actualizar miembro");
      }
    } catch (e) {
      toast.error("Error al actualizar miembro");
    }
  };

  const handleDeleteApiKey = async (id: number) => {
    if (!(await confirmAction({ title: 'Eliminar API Key', message: '¿Estás seguro de eliminar esta API Key permanentemente?', type: 'danger', confirmText: 'Eliminar' }))) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/api-keys/${id}?org_id=${orgId}`, {
        method: 'DELETE',
        headers
      }).then(r => r.json());
      if (res.success) {
        toast.success("API Key eliminada correctamente");
        fetchApiKeysAndLogs();
      } else {
        toast.error(res.message || "Error al eliminar API Key");
      }
    } catch (e) {
      toast.error("Error al eliminar API Key");
    }
  };

  const handleDeleteArea = async (id: number) => {
    if (!(await confirmAction({ title: 'Eliminar área', message: '¿Estás seguro de eliminar esta área? Se desvincularán los responsables asociados.', type: 'danger', confirmText: 'Eliminar' }))) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/areas/${id}?org_id=${orgId}`, { method: 'DELETE', headers }).then(r => r.json());
      if (res.success) {
        toast.success("Área eliminada correctamente");
        fetchCatalogs();
      } else {
        toast.error("Error al eliminar área");
      }
    } catch (e) {
      toast.error("Error al conectar con el servidor");
    }
  };

  const handleDeleteResponsible = async (id: number) => {
    if (!(await confirmAction({ title: 'Eliminar responsable', message: '¿Estás seguro de eliminar este responsable?', type: 'danger', confirmText: 'Eliminar' }))) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/responsibles/${id}?org_id=${orgId}`, { method: 'DELETE', headers }).then(r => r.json());
      if (res.success) {
        toast.success("Responsable eliminado correctamente");
        fetchCatalogs();
      } else {
        toast.error("Error al eliminar responsable");
      }
    } catch (e) {
      toast.error("Error al conectar con el servidor");
    }
  };

  const handleDeleteDocType = async (id: number) => {
    if (!(await confirmAction({ title: 'Eliminar tipo de documento', message: '¿Estás seguro de eliminar este tipo de documento?', type: 'danger', confirmText: 'Eliminar' }))) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/document-types/${id}?org_id=${orgId}`, { method: 'DELETE', headers }).then(r => r.json());
      if (res.success) {
        toast.success("Tipo de documento eliminado");
        fetchCatalogs();
      } else {
        toast.error("Error al eliminar");
      }
    } catch (e) {
      toast.error("Error al conectar con el servidor");
    }
  };

  const handleDeleteDocStatus = async (id: number) => {
    if (!(await confirmAction({ title: 'Eliminar estado', message: '¿Estás seguro de eliminar este estado de documento?', type: 'danger', confirmText: 'Eliminar' }))) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/document-statuses/${id}?org_id=${orgId}`, { method: 'DELETE', headers }).then(r => r.json());
      if (res.success) {
        toast.success("Estado de documento eliminado");
        fetchCatalogs();
      } else {
        toast.error("Error al eliminar");
      }
    } catch (e) {
      toast.error("Error al conectar con el servidor");
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!(await confirmAction({ title: 'Eliminar plantilla', message: '¿Estás seguro de eliminar esta plantilla?', type: 'danger', confirmText: 'Eliminar' }))) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/notification-templates/${id}?org_id=${orgId}`, { method: 'DELETE', headers }).then(r => r.json());
      if (res.success) {
        toast.success("Plantilla eliminada correctamente");
        fetchEventsAndRules();
      } else {
        toast.error("Error al eliminar plantilla");
      }
    } catch (e) {
      toast.error("Error al conectar con el servidor");
    }
  };

  const handleDeleteRule = async (id: number) => {
    if (!(await confirmAction({ title: 'Eliminar regla', message: '¿Estás seguro de eliminar esta regla?', type: 'danger', confirmText: 'Eliminar' }))) return;
    try {
      const headers = { 'x-user-id': currentUser.id.toString(), 'Content-Type': 'application/json' };
      const res = await fetch(`/api/org/notification-rules/${id}?org_id=${orgId}`, { method: 'DELETE', headers }).then(r => r.json());
      if (res.success) {
        toast.success("Regla eliminada correctamente");
        fetchEventsAndRules();
      } else {
        toast.error("Error al eliminar regla");
      }
    } catch (e) {
      toast.error("Error al conectar con el servidor");
    }
  };

  const isSuperAdmin = currentUser?.user_type === 'super_admin' || currentUser?.role === 'super_admin' || currentUser?.email === 'daviex14@gmail.com';

  return (
    <div className="space-y-6">
      {isSuperAdmin && organizations.length > 0 && (
        <div className="bg-gradient-to-r from-primary-600 to-indigo-600 p-4 rounded-xl border border-primary-500 shadow-md flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
              Consola de Súper Administrador
            </h2>
            <p className="text-xs text-white/90 mt-0.5">Gestiona y configura los catálogos, reglas y API keys de cualquier organización activa.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/90 font-semibold uppercase tracking-wider">Organización Activa:</span>
            <select
              value={orgId}
              onChange={(e) => setOrgId(Number(e.target.value))}
              className="bg-white/10 text-white rounded-lg px-4 py-2 text-sm border border-white/20 focus:outline-none focus:border-white font-semibold min-w-[220px] backdrop-blur-sm hover:bg-white/20 transition-all [&>option]:text-slate-800"
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name} ({org.slug.toUpperCase()})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Sub tabs header */}
      <div className="flex flex-wrap gap-2 bg-slate-100 p-2 rounded-xl border border-slate-200">
        <button 
          onClick={() => setActiveSubTab('blueprints')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeSubTab === 'blueprints' ? 'bg-primary-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
        >
          <Database size={18} />
          Blueprints / Semillas
        </button>
        <button 
          onClick={() => setActiveSubTab('catalogs')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeSubTab === 'catalogs' ? 'bg-primary-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
        >
          <Briefcase size={18} />
          Estructura y Catálogos
        </button>
        <button 
          onClick={() => setActiveSubTab('events_rules')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeSubTab === 'events_rules' ? 'bg-primary-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
        >
          <FileText size={18} />
          Eventos & Reglas
        </button>
        <button 
          onClick={() => setActiveSubTab('api_keys')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeSubTab === 'api_keys' ? 'bg-primary-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
        >
          <Key size={18} />
          Integración & API Keys
        </button>
        <button 
          onClick={() => setActiveSubTab('reports')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeSubTab === 'reports' ? 'bg-primary-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
        >
          <BarChart3 size={18} />
          Reportes & Consumo
        </button>
        <button 
          onClick={() => setActiveSubTab('members')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeSubTab === 'members' ? 'bg-primary-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
        >
          <Users size={18} />
          Miembros / Accesos
        </button>
        {isSuperAdmin && (
          <button 
            onClick={() => setActiveSubTab('global_control')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeSubTab === 'global_control' ? 'bg-primary-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
          >
            <Settings size={18} />
            Control Global
          </button>
        )}
      </div>

      {/* Blueprints Content */}
      {activeSubTab === 'blueprints' && (
        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-slate-800">Blueprints de Inicialización</h3>
            <p className="text-sm text-slate-600 mt-1">
              Selecciona una plantilla base para configurar automáticamente las áreas, estados, tipos documentarios, schemas de eventos, plantillas y reglas de notificación.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 hover:border-primary-500/50 transition-all flex flex-col justify-between">
              <div>
                <span className="bg-emerald-500/10 text-emerald-400 text-xs px-2.5 py-1 rounded-full font-semibold">GUBERNAMENTAL</span>
                <h4 className="text-lg font-bold text-slate-800 mt-3">Gestión Documentaria (Tipo GORE)</h4>
                <p className="text-sm text-slate-600 mt-2">
                  Estructura predeterminada para Gobiernos Regionales o Municipalidades. Incluye áreas como Mesa de Partes, Logística, y Gerencia Regional, junto a eventos de derivación de expedientes.
                </p>
              </div>
              <button 
                onClick={() => applyBlueprint('government_document_management')}
                className="mt-6 w-full bg-primary-600 hover:bg-primary-500 text-white py-2.5 rounded-lg font-medium transition-colors"
              >
                Aplicar Blueprint
              </button>
            </div>

            <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-60 hover:opacity-100 transition-all flex flex-col justify-between">
              <div>
                <span className="bg-blue-500/10 text-blue-400 text-xs px-2.5 py-1 rounded-full font-semibold">CORPORATIVO</span>
                <h4 className="text-lg font-bold text-slate-800 mt-3">Atención al Cliente Corporativo</h4>
                <p className="text-sm text-slate-600 mt-2">
                  Estructura dinámica para e-commerce o soporte técnico comercial. Incluye áreas de Soporte, Ventas y Facturación con alertas automáticas de pedidos.
                </p>
              </div>
              <button 
                disabled 
                className="mt-6 w-full bg-slate-200 text-slate-400 py-2.5 rounded-lg font-medium cursor-not-allowed"
              >
                Próximamente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Catalogs Content */}
      {activeSubTab === 'catalogs' && (
        <div className="space-y-6">
          {/* Search bar */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-4 flex items-center gap-3">
            <Search className="text-slate-400 shrink-0" size={18} />
            <input 
              type="text" 
              placeholder="Buscar áreas o responsables por nombre, código, cargo o teléfono..." 
              value={catalogSearchQuery}
              onChange={e => setCatalogSearchQuery(e.target.value)}
              className="w-full bg-transparent text-slate-800 text-sm focus:outline-none placeholder-slate-400"
            />
            {catalogSearchQuery && (
              <button 
                onClick={() => setCatalogSearchQuery('')}
                className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded bg-slate-100"
              >
                Limpiar
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Areas Section */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Briefcase size={20} className="text-primary-500" />
                Áreas de la Organización
              </h3>

              {/* List */}
              <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                {areas.filter(a => 
                  a.name.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || 
                  a.code.toLowerCase().includes(catalogSearchQuery.toLowerCase()) ||
                  (a.description && a.description.toLowerCase().includes(catalogSearchQuery.toLowerCase()))
                ).length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No se encontraron áreas.</p>
                ) : (
                  areas.filter(a => 
                    a.name.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || 
                    a.code.toLowerCase().includes(catalogSearchQuery.toLowerCase()) ||
                    (a.description && a.description.toLowerCase().includes(catalogSearchQuery.toLowerCase()))
                  ).map(a => (
                    <div key={a.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex flex-col gap-2">
                      {editingArea?.id === a.id ? (
                        <div className="space-y-3">
                          <p className="text-xs font-bold text-slate-500 uppercase">Editar Área</p>
                          <div className="grid grid-cols-2 gap-2">
                            <input 
                              type="text" 
                              value={editingArea.name}
                              onChange={e => setEditingArea({...editingArea, name: e.target.value})}
                              placeholder="Nombre"
                              className="bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                            <input 
                              type="text" 
                              value={editingArea.code}
                              onChange={e => setEditingArea({...editingArea, code: e.target.value})}
                              placeholder="Código"
                              className="bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                          </div>
                          <input 
                            type="text" 
                            value={editingArea.description}
                            onChange={e => setEditingArea({...editingArea, description: e.target.value})}
                            placeholder="Descripción"
                            className="w-full bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                          />
                          <div className="flex justify-end gap-2 text-xs">
                            <button 
                              type="button" 
                              onClick={() => handleUpdateArea(a.id, editingArea.name, editingArea.code, editingArea.description)}
                              className="bg-primary-600 hover:bg-primary-550 text-white px-3 py-1.5 rounded font-semibold"
                            >
                              Guardar
                            </button>
                            <button 
                              type="button" 
                              onClick={() => setEditingArea(null)}
                              className="bg-slate-200 hover:bg-slate-305 text-slate-700 px-3 py-1.5 rounded font-semibold"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{a.name}</p>
                            <p className="text-xs text-slate-650">{a.code} • {a.description || 'Sin descripción'}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button 
                              onClick={() => setEditingArea({ id: a.id, name: a.name, code: a.code, description: a.description || '' })}
                              className="text-primary-600 hover:text-primary-800 hover:bg-primary-50 p-1.5 rounded-lg transition-colors"
                              title="Editar Área"
                            >
                              <Plus size={16} className="rotate-45" /> {/* Use Plus rotated or similar, let's keep icon simple, we have settings or we can edit. Wait, we can import Edit2 from lucide-react later if we want but wait, we have Lucide icons already. Let's use simple edit icon or a pencil */}
                              <Settings size={16} />
                            </button>
                            <button 
                              onClick={() => handleDeleteArea(a.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                              title="Eliminar Área"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Form */}
              <form onSubmit={handleAddArea} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nueva Área</p>
                <div className="grid grid-cols-2 gap-2">
                  <input 
                    type="text" 
                    placeholder="Nombre" 
                    value={newArea.name}
                    onChange={e => setNewArea({...newArea, name: e.target.value})}
                    className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                    required
                  />
                  <input 
                    type="text" 
                    placeholder="Código" 
                    value={newArea.code}
                    onChange={e => setNewArea({...newArea, code: e.target.value})}
                    className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                    required
                  />
                </div>
                <input 
                  type="text" 
                  placeholder="Descripción" 
                  value={newArea.description}
                  onChange={e => setNewArea({...newArea, description: e.target.value})}
                  className="w-full bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                />
                <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1">
                  <Plus size={16} /> Agregar Área
                </button>
              </form>
            </div>

            {/* Responsibles Section */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Users size={20} className="text-primary-500" />
                Responsables y Contactos
              </h3>

              {/* List */}
              <div className="max-h-85 overflow-y-auto space-y-2 pr-1">
                {responsibles.filter(r => 
                  r.full_name.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || 
                  (r.position && r.position.toLowerCase().includes(catalogSearchQuery.toLowerCase())) ||
                  r.whatsapp_phone.includes(catalogSearchQuery)
                ).length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No se encontraron responsables.</p>
                ) : (
                  responsibles.filter(r => 
                    r.full_name.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || 
                    (r.position && r.position.toLowerCase().includes(catalogSearchQuery.toLowerCase())) ||
                    r.whatsapp_phone.includes(catalogSearchQuery)
                  ).map(r => (
                    <div key={r.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex flex-col gap-2">
                      {editingResponsible?.id === r.id ? (
                        <div className="space-y-3">
                          <p className="text-xs font-bold text-slate-500 uppercase">Editar Responsable</p>
                          <div className="grid grid-cols-2 gap-2">
                            <input 
                              type="text" 
                              value={editingResponsible.fullName}
                              onChange={e => setEditingResponsible({...editingResponsible, fullName: e.target.value})}
                              placeholder="Nombre Completo"
                              className="bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                            <input 
                              type="text" 
                              value={editingResponsible.whatsappPhone}
                              onChange={e => setEditingResponsible({...editingResponsible, whatsappPhone: e.target.value})}
                              placeholder="Teléfono"
                              className="bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input 
                              type="text" 
                              value={editingResponsible.position}
                              onChange={e => setEditingResponsible({...editingResponsible, position: e.target.value})}
                              placeholder="Cargo"
                              className="bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                            <select 
                              value={editingResponsible.areaId}
                              onChange={e => setEditingResponsible({...editingResponsible, areaId: e.target.value})}
                              className="bg-white text-slate-800 rounded px-2 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            >
                              <option value="">Seleccionar Área</option>
                              {areas.map(a => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                              ))}
                            </select>
                          </div>
                          <input 
                            type="email" 
                            value={editingResponsible.email}
                            onChange={e => setEditingResponsible({...editingResponsible, email: e.target.value})}
                            placeholder="Email (Opcional)"
                            className="w-full bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                          />
                          <div className="flex justify-end gap-2 text-xs">
                            <button 
                              type="button" 
                              onClick={() => handleUpdateResponsible(r.id, editingResponsible.fullName, editingResponsible.position, editingResponsible.whatsappPhone, editingResponsible.email, editingResponsible.areaId)}
                              className="bg-primary-600 hover:bg-primary-550 text-white px-3 py-1.5 rounded font-semibold"
                            >
                              Guardar
                            </button>
                            <button 
                              type="button" 
                              onClick={() => setEditingResponsible(null)}
                              className="bg-slate-200 hover:bg-slate-305 text-slate-700 px-3 py-1.5 rounded font-semibold"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{r.full_name}</p>
                            <p className="text-xs text-slate-650">{r.position || 'Responsable'} • {r.whatsapp_phone}</p>
                            {r.area_id && (
                              <span className="inline-block bg-primary-100 text-primary-600 text-[10px] px-1.5 py-0.5 rounded mt-1 font-semibold">
                                Área: {areas.find(a => a.id === r.area_id)?.name || 'Cargando...'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button 
                              onClick={() => setEditingResponsible({ id: r.id, fullName: r.full_name, whatsappPhone: r.whatsapp_phone, position: r.position || '', email: r.email || '', areaId: r.area_id || '' })}
                              className="text-primary-600 hover:text-primary-800 hover:bg-primary-50 p-1.5 rounded-lg transition-colors"
                              title="Editar Responsable"
                            >
                              <Settings size={16} />
                            </button>
                            <button 
                              onClick={() => handleDeleteResponsible(r.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                              title="Eliminar Responsable"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Form */}
              <form onSubmit={handleAddResponsible} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nuevo Responsable</p>
                <div className="grid grid-cols-2 gap-2">
                  <input 
                    type="text" 
                    placeholder="Nombre Completo" 
                    value={newResponsible.fullName}
                    onChange={e => setNewResponsible({...newResponsible, fullName: e.target.value})}
                    className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                    required
                  />
                  <input 
                    type="text" 
                    placeholder="Teléfono (+51...)" 
                    value={newResponsible.whatsappPhone}
                    onChange={e => setNewResponsible({...newResponsible, whatsappPhone: e.target.value})}
                    className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input 
                    type="text" 
                    placeholder="Cargo" 
                    value={newResponsible.position}
                    onChange={e => setNewResponsible({...newResponsible, position: e.target.value})}
                    className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                  />
                  <select 
                    value={newResponsible.areaId}
                    onChange={e => setNewResponsible({...newResponsible, areaId: e.target.value})}
                    className="bg-white text-slate-700 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                  >
                    <option value="">Seleccionar Área</option>
                    {areas.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1">
                  <Plus size={16} /> Agregar Responsable
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Events & Rules Content */}
      {activeSubTab === 'events_rules' && (
        <div className="space-y-6">
          {/* Search bar */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-4 flex items-center gap-3">
            <Search className="text-slate-400 shrink-0" size={18} />
            <input 
              type="text" 
              placeholder="Buscar por nombre, código de plantilla o tipo de evento..." 
              value={eventsRulesSearchQuery}
              onChange={e => setEventsRulesSearchQuery(e.target.value)}
              className="w-full bg-transparent text-slate-800 text-sm focus:outline-none placeholder-slate-400"
            />
            {eventsRulesSearchQuery && (
              <button 
                onClick={() => setEventsRulesSearchQuery('')}
                className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded bg-slate-100"
              >
                Limpiar
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Event Schemas */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-4 lg:col-span-1">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Database size={20} className="text-primary-500" />
                Eventos Registrados
              </h3>
              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                {eventSchemas.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No hay esquemas de eventos activos.</p>
                ) : (
                  eventSchemas.map(es => (
                    <div key={es.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <span className="bg-primary-500/10 text-primary-400 text-xs px-2 py-0.5 rounded-full font-semibold">{es.entity_type || 'General'}</span>
                      <h4 className="text-sm font-bold text-slate-800 mt-2">{es.name}</h4>
                      <p className="text-xs text-slate-650 font-mono mt-1">{es.event_type}</p>
                      <p className="text-xs text-slate-650 mt-2">{es.description}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Templates & Rules List */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-4 lg:col-span-2">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FileText size={20} className="text-primary-500" />
                Plantillas & Reglas Activas
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Templates Panel */}
                <div className="space-y-3 border-r border-slate-200 pr-2">
                  <p className="text-sm font-bold text-slate-800">Plantillas de WhatsApp</p>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                    {templates.filter(t => 
                      t.name.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()) ||
                      t.code.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()) ||
                      (t.body && t.body.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()))
                    ).length === 0 ? (
                      <p className="text-xs text-slate-500">No se encontraron plantillas.</p>
                    ) : (
                      templates.filter(t => 
                        t.name.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()) ||
                        t.code.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()) ||
                        (t.body && t.body.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()))
                      ).map(t => (
                        <div key={t.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex flex-col gap-2">
                          {editingTemplate?.id === t.id ? (
                            <div className="space-y-2">
                              <p className="text-[10px] font-bold text-slate-400 uppercase">Editar Plantilla</p>
                              <input 
                                type="text" 
                                value={editingTemplate.name}
                                onChange={e => setEditingTemplate({...editingTemplate, name: e.target.value})}
                                placeholder="Nombre"
                                className="w-full bg-white text-xs px-2.5 py-1.5 border border-slate-250 rounded focus:outline-none"
                              />
                              <input 
                                type="text" 
                                value={editingTemplate.code}
                                onChange={e => setEditingTemplate({...editingTemplate, code: e.target.value})}
                                placeholder="Código"
                                className="w-full bg-white text-xs px-2.5 py-1.5 border border-slate-250 rounded focus:outline-none"
                              />
                              <textarea 
                                value={editingTemplate.body}
                                onChange={e => setEditingTemplate({...editingTemplate, body: e.target.value})}
                                placeholder="Cuerpo de la plantilla"
                                className="w-full bg-white text-xs px-2.5 py-1.5 border border-slate-250 rounded focus:outline-none h-16 resize-none"
                              />
                              <div className="flex justify-end gap-2 text-[10px]">
                                <button 
                                  type="button" 
                                  onClick={() => handleUpdateTemplate(t.id, editingTemplate.name, editingTemplate.code, editingTemplate.body)}
                                  className="bg-primary-600 text-white px-2 py-1 rounded font-semibold"
                                >
                                  Guardar
                                </button>
                                <button 
                                  type="button" 
                                  onClick={() => setEditingTemplate(null)}
                                  className="bg-slate-200 text-slate-700 px-2 py-1 rounded font-semibold"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-between items-start gap-2">
                              <div>
                                <p className="text-xs font-bold text-slate-800">{t.name} ({t.code})</p>
                                <p className="text-xs text-slate-650 mt-1 line-clamp-2 italic">"{t.body || 'Meta Template'}"</p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button 
                                  onClick={() => setEditingTemplate({ id: t.id, name: t.name, code: t.code, body: t.body || '' })}
                                  className="text-primary-600 hover:text-primary-850 hover:bg-primary-50 p-1 rounded transition-colors"
                                  title="Editar Plantilla"
                                >
                                  <Settings size={14} />
                                </button>
                                <button 
                                  onClick={() => handleDeleteTemplate(t.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                                  title="Eliminar Plantilla"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Rules Panel */}
                <div className="space-y-3">
                  <p className="text-sm font-bold text-slate-800">Reglas de Notificación</p>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                    {rules.filter(r => 
                      r.name.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()) ||
                      r.event_type.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()) ||
                      r.recipient_mode.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase())
                    ).length === 0 ? (
                      <p className="text-xs text-slate-500">No se encontraron reglas.</p>
                    ) : (
                      rules.filter(r => 
                        r.name.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()) ||
                        r.event_type.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase()) ||
                        r.recipient_mode.toLowerCase().includes(eventsRulesSearchQuery.toLowerCase())
                      ).map(r => (
                        <div key={r.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex flex-col gap-2">
                          {editingRule?.id === r.id ? (
                            <div className="space-y-2">
                              <p className="text-[10px] font-bold text-slate-400 uppercase">Editar Regla</p>
                              <input 
                                type="text" 
                                value={editingRule.name}
                                onChange={e => setEditingRule({...editingRule, name: e.target.value})}
                                placeholder="Nombre"
                                className="w-full bg-white text-xs px-2.5 py-1.5 border border-slate-250 rounded focus:outline-none"
                              />
                              <input 
                                type="text" 
                                value={editingRule.eventType}
                                onChange={e => setEditingRule({...editingRule, eventType: e.target.value})}
                                placeholder="Tipo de evento"
                                className="w-full bg-white text-xs px-2.5 py-1.5 border border-slate-250 rounded focus:outline-none"
                              />
                              <select 
                                value={editingRule.recipientMode}
                                onChange={e => setEditingRule({...editingRule, recipientMode: e.target.value})}
                                className="w-full bg-white text-xs px-2 py-1.5 border border-slate-250 rounded focus:outline-none"
                              >
                                <option value="responsible">Responsable de Área</option>
                                <option value="citizen">Ciudadano / Destinatario</option>
                                <option value="both">Ambos</option>
                              </select>
                              <div className="flex justify-end gap-2 text-[10px]">
                                <button 
                                  type="button" 
                                  onClick={() => handleUpdateRule(r.id, editingRule.name, editingRule.eventType, editingRule.recipientMode)}
                                  className="bg-primary-600 text-white px-2 py-1 rounded font-semibold"
                                >
                                  Guardar
                                </button>
                                <button 
                                  type="button" 
                                  onClick={() => setEditingRule(null)}
                                  className="bg-slate-200 text-slate-700 px-2 py-1 rounded font-semibold"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-between items-start gap-2">
                              <div>
                                <p className="text-xs font-bold text-slate-800">{r.name}</p>
                                <p className="text-xs text-slate-650 mt-0.5">Filtro: {r.event_type}</p>
                                <span className="inline-block bg-emerald-500/10 text-emerald-600 text-[10px] px-1.5 py-0.5 rounded-md mt-1 font-semibold">
                                  Modo: {r.recipient_mode}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button 
                                  onClick={() => setEditingRule({ id: r.id, name: r.name, eventType: r.event_type, recipientMode: r.recipient_mode })}
                                  className="text-primary-600 hover:text-primary-850 hover:bg-primary-50 p-1 rounded transition-colors"
                                  title="Editar Regla"
                                >
                                  <Settings size={14} />
                                </button>
                                <button 
                                  onClick={() => handleDeleteRule(r.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                                  title="Eliminar Regla"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integration & API Keys Content */}
      {activeSubTab === 'api_keys' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* API Keys Manage */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-4 lg:col-span-1">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Key size={20} className="text-primary-500" />
              API Keys del Canal
            </h3>

            {generatedKey && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-xl space-y-2">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle size={16} />
                  <span className="text-xs font-bold">Guarda esta clave en un lugar seguro:</span>
                </div>
                <p className="text-xs font-mono bg-slate-950 p-2 rounded text-white break-all select-all">{generatedKey}</p>
                <p className="text-[10px] text-slate-400">Solo se mostrará esta vez.</p>
              </div>
            )}

            {/* Search for API Keys */}
            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs shadow-sm mb-3">
              <Search size={14} className="text-slate-400" />
              <input 
                type="text" 
                placeholder="Buscar API Keys..." 
                value={apiKeySearchQuery}
                onChange={e => setApiKeySearchQuery(e.target.value)}
                className="bg-transparent text-xs text-slate-800 focus:outline-none w-full"
              />
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {apiKeys.filter(k => 
                k.name.toLowerCase().includes(apiKeySearchQuery.toLowerCase()) ||
                k.api_key_prefix.toLowerCase().includes(apiKeySearchQuery.toLowerCase())
              ).length === 0 ? (
                <p className="text-sm text-slate-505 text-center py-4">No se encontraron API Keys.</p>
              ) : (
                apiKeys.filter(k => 
                  k.name.toLowerCase().includes(apiKeySearchQuery.toLowerCase()) ||
                  k.api_key_prefix.toLowerCase().includes(apiKeySearchQuery.toLowerCase())
                ).map(k => (
                  <div key={k.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex justify-between items-center hover:border-slate-300 transition-colors">
                    <div>
                      <p className="text-xs font-bold text-slate-850">{k.name}</p>
                      <p className="text-[10px] text-slate-500 font-mono">Prefijo: {k.api_key_prefix}...</p>
                      <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded font-semibold mt-1 ${k.status === 'active' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-650 border border-red-100'}`}>
                        {k.status.toUpperCase()}
                      </span>
                    </div>
                    {k.status === 'active' && (
                      <button 
                        onClick={() => handleDeleteApiKey(k.id)}
                        className="text-red-500 hover:text-red-750 hover:bg-red-50 p-1.5 rounded-lg transition-colors shrink-0"
                        title="Eliminar/Revocar API Key"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleGenerateApiKey} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Generar Clave</p>
              <input 
                type="text" 
                placeholder="Nombre de la Integración (ej. CRM)" 
                value={newApiKey.name}
                onChange={e => setNewApiKey({...newApiKey, name: e.target.value})}
                className="w-full bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
              />
              <button type="submit" className="w-full bg-primary-600 hover:bg-primary-500 text-white py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1">
                Generar API Key
              </button>
            </form>
          </div>

          {/* Test Integrations Tool */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-4 lg:col-span-2">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Send size={20} className="text-primary-500" />
              Consola de Pruebas Webhook (Endpoint Universal)
            </h3>
            <p className="text-xs text-slate-600">
              Puedes probar tus reglas enviando un payload directo a `POST /api/integrations/events` usando Bearer Authentication.
            </p>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
              <p className="text-xs font-bold text-slate-300">Ejemplo de Payload de Cambio de Estado:</p>
              <pre className="text-xs bg-slate-950 p-3 rounded-lg text-emerald-400 font-mono overflow-x-auto">
{`{
  "event_type": "document.status_changed",
  "entity_type": "document",
  "document_code": "EXP-2026-0004",
  "document_type_id": 1,
  "status_id": 2,
  "area_destination_id": 1,
  "recipient": {
    "name": "David",
    "whatsapp_phone": "+51942629785"
  }
}`}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Reports Content */}
      {activeSubTab === 'reports' && (
        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-slate-800">Reporte de Notificaciones e Historial</h3>
            <p className="text-sm text-slate-600 mt-1">Estadísticas estimadas de consumo del canal.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center">
              <p className="text-xs font-bold text-slate-400 uppercase">Enviados (Total)</p>
              <p className="text-3xl font-extrabold text-slate-800 mt-2">{reportMetrics.total_messages || 0}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center">
              <p className="text-xs font-bold text-slate-400 uppercase">Entregados</p>
              <p className="text-3xl font-extrabold text-emerald-600 mt-2">{reportMetrics.delivered || 0}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center">
              <p className="text-xs font-bold text-slate-400 uppercase">Leídos</p>
              <p className="text-3xl font-extrabold text-blue-600 mt-2">{reportMetrics.read || 0}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center">
              <p className="text-xs font-bold text-slate-400 uppercase">Costo Est. Meta API</p>
              <p className="text-3xl font-extrabold text-amber-500 mt-2">${(reportMetrics.estimated_cost_usd || 0).toFixed(2)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
            {/* Visual Bar Chart */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4 shadow-sm">
              <p className="text-sm font-bold text-slate-800">Estado de Envío</p>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs text-slate-650 mb-1">
                    <span>Mensajes Exitosos (Sent/Delivered/Read)</span>
                    <span className="font-bold text-slate-850">
                      {((reportMetrics.delivered || 0) + (reportMetrics.read || 0) + (reportMetrics.sent || 0))} msgs
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                    <div className="bg-primary-500 h-full rounded-full" style={{ width: `${reportMetrics.total_messages ? Math.min(100, Math.round((((reportMetrics.delivered || 0) + (reportMetrics.read || 0)) / reportMetrics.total_messages) * 100)) : 0}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-slate-655 mb-1">
                    <span>Fallidos</span>
                    <span className="font-bold text-slate-850">{reportMetrics.failed || 0} msgs</span>
                  </div>
                  <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                    <div className="bg-red-500 h-full rounded-full" style={{ width: `${reportMetrics.total_messages ? Math.round(((reportMetrics.failed || 0) / reportMetrics.total_messages) * 100) : 0}%` }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Provider comparison visual */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4 shadow-sm">
              <p className="text-sm font-bold text-slate-800">Distribución de Proveedor</p>
              <div className="flex items-center justify-around h-32">
                <div className="text-center">
                  <p className="text-xs text-slate-600 uppercase font-bold">WhatsMeow (WhatsApp Web)</p>
                  <p className="text-2xl font-extrabold text-slate-850 mt-1">
                    {reportMetrics.total_messages ? Math.round(((reportMetrics.provider_whatsmeow || 0) / reportMetrics.total_messages) * 100) : 0}%
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Envío gratuito</p>
                </div>
                <div className="h-16 w-px bg-slate-300"></div>
                <div className="text-center">
                  <p className="text-xs text-slate-600 uppercase font-bold">Meta Cloud API (Official)</p>
                  <p className="text-2xl font-extrabold text-amber-500 mt-1">
                    {reportMetrics.total_messages ? Math.round(((reportMetrics.provider_meta || 0) / reportMetrics.total_messages) * 100) : 0}%
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Plantilla oficial</p>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar for logs */}
          <div className="border-t border-slate-100 pt-6 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h4 className="text-lg font-bold text-slate-800">Historial Reciente de Notificaciones</h4>
              
              <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-250 w-full md:w-80 shadow-sm">
                <Search size={16} className="text-slate-400" />
                <input 
                  type="text" 
                  placeholder="Buscar destinatario, teléfono o mensaje..." 
                  value={reportsSearchQuery}
                  onChange={e => setReportsSearchQuery(e.target.value)}
                  className="bg-transparent text-xs text-slate-850 focus:outline-none w-full"
                />
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                <thead className="bg-slate-50 text-slate-700 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3">Fecha/Hora</th>
                    <th className="px-4 py-3">Destinatario</th>
                    <th className="px-4 py-3">Teléfono</th>
                    <th className="px-4 py-3">Proveedor</th>
                    <th className="px-4 py-3">Previsualización</th>
                    <th className="px-4 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white text-slate-650">
                  {reportLogs.filter(log => {
                    const search = reportsSearchQuery.toLowerCase();
                    return (
                      (log.recipient_name && log.recipient_name.toLowerCase().includes(search)) ||
                      log.recipient_phone.includes(search) ||
                      (log.content_preview && log.content_preview.toLowerCase().includes(search)) ||
                      log.provider.toLowerCase().includes(search) ||
                      log.status.toLowerCase().includes(search)
                    );
                  }).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-slate-500">No se encontraron registros de envío.</td>
                    </tr>
                  ) : (
                    reportLogs.filter(log => {
                      const search = reportsSearchQuery.toLowerCase();
                      return (
                        (log.recipient_name && log.recipient_name.toLowerCase().includes(search)) ||
                        log.recipient_phone.includes(search) ||
                        (log.content_preview && log.content_preview.toLowerCase().includes(search)) ||
                        log.provider.toLowerCase().includes(search) ||
                        log.status.toLowerCase().includes(search)
                      );
                    }).map(log => (
                      <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3 font-semibold text-slate-800">{log.recipient_name || 'Ciudadano'}</td>
                        <td className="px-4 py-3 font-mono">{log.recipient_phone}</td>
                        <td className="px-4 py-3 uppercase">{log.provider === 'meta_cloud_api' ? 'Meta Cloud' : 'WhatsMeow'}</td>
                        <td className="px-4 py-3 truncate max-w-xs" title={log.content_preview}>{log.content_preview || 'Sin vista previa'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full font-bold text-[10px] ${
                            log.status === 'read' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                            log.status === 'delivered' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                            log.status === 'sent' ? 'bg-slate-100 text-slate-700' :
                            'bg-red-50 text-red-600 border border-red-100'
                          }`}>
                            {log.status.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Global Control Content (Super Admin Only) */}
      {isSuperAdmin && activeSubTab === 'global_control' && (
        <div className="space-y-6">
          {/* Help Info Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex gap-3 shadow-sm">
            <Info className="text-blue-500 shrink-0 mt-0.5" size={20} />
            <div className="space-y-1">
              <h4 className="text-sm font-bold text-blue-900">¿Cómo funciona y cómo lo usan las organizaciones?</h4>
              <p className="text-xs text-blue-700 leading-relaxed">
                Como Súper Administrador puedes crear nuevas organizaciones, editar sus metadatos y eliminarlas permanentemente. También puedes vincular usuarios a ellas mediante membresías y roles.
              </p>
              <p className="text-xs text-blue-700 leading-relaxed">
                Cuando un usuario asignado a una organización inicia sesión, la plataforma detecta automáticamente su organización. En su barra lateral verá el menú como <strong>"Mi Organización"</strong> y solo podrá ver y configurar los catálogos (áreas, responsables), reglas, plantillas y API keys correspondientes a su organización, manteniéndose completamente aislado del resto.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column: Create & List Organizations */}
            <div className="space-y-6">
              {/* Create Organization Form */}
              <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-4">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Briefcase size={20} className="text-primary-500" />
                  Registrar Nueva Organización
                </h3>
                
                <form onSubmit={handleAddOrg} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-500 uppercase">Nombre Comercial</label>
                      <input 
                        type="text" 
                        placeholder="Ej. GORE HUANUCO" 
                        value={newOrg.name}
                        onChange={e => setNewOrg({...newOrg, name: e.target.value})}
                        className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-500 uppercase">Slug Único (URL/Identificador)</label>
                      <input 
                        type="text" 
                        placeholder="Ej. gorehco" 
                        value={newOrg.slug}
                        onChange={e => setNewOrg({...newOrg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '')})}
                        className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-500 uppercase">Tipo</label>
                      <select
                        value={newOrg.type}
                        onChange={e => setNewOrg({...newOrg, type: e.target.value})}
                        className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                      >
                        <option value="corporate">Corporativo / Privado</option>
                        <option value="government">Gubernamental / Público</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-500 uppercase">RUC (Opcional)</label>
                      <input 
                        type="text" 
                        placeholder="Número de 11 dígitos" 
                        value={newOrg.ruc}
                        onChange={e => setNewOrg({...newOrg, ruc: e.target.value.replace(/[^0-9]/g, '')})}
                        className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 my-2 pt-2">
                    <p className="text-xs font-bold text-slate-400 mb-2 uppercase">Información de Contacto</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-slate-500 uppercase">Nombre del Contacto</label>
                        <input 
                          type="text" 
                          placeholder="Ej. Juan Pérez" 
                          value={newOrg.contactName}
                          onChange={e => setNewOrg({...newOrg, contactName: e.target.value})}
                          className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-slate-500 uppercase">Teléfono Móvil</label>
                        <input 
                          type="text" 
                          placeholder="Ej. +51..." 
                          value={newOrg.contactPhone}
                          onChange={e => setNewOrg({...newOrg, contactPhone: e.target.value})}
                          className="bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 mt-2">
                      <label className="text-xs font-bold text-slate-500 uppercase">Email</label>
                      <input 
                        type="email" 
                        placeholder="contacto@organizacion.com" 
                        value={newOrg.contactEmail}
                        onChange={e => setNewOrg({...newOrg, contactEmail: e.target.value})}
                        className="w-full bg-white text-slate-800 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full mt-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 text-white py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1 shadow-sm"
                  >
                    <Plus size={16} /> Registrar Organización
                  </button>
                </form>
              </div>

              {/* List of Registered Organizations */}
              <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-slate-800">Organizaciones Registradas</h3>
                  <span className="bg-slate-100 text-slate-700 text-xs px-2.5 py-0.5 rounded-full font-bold">{organizations.length}</span>
                </div>

                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex items-center gap-2">
                  <Search size={16} className="text-slate-400" />
                  <input 
                    type="text" 
                    placeholder="Buscar organización por nombre o slug..." 
                    value={orgSearchQuery}
                    onChange={e => setOrgSearchQuery(e.target.value)}
                    className="w-full bg-transparent text-xs text-slate-800 focus:outline-none"
                  />
                </div>

                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {organizations.filter(o => 
                    o.name.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
                    o.slug.toLowerCase().includes(orgSearchQuery.toLowerCase())
                  ).map(o => (
                    <div key={o.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col gap-3">
                      {editingOrg?.id === o.id ? (
                        <div className="space-y-3">
                          <p className="text-xs font-bold text-slate-500 uppercase">Editar Organización</p>
                          <div className="grid grid-cols-2 gap-2">
                            <input 
                              type="text" 
                              value={editingOrg.name}
                              onChange={e => setEditingOrg({...editingOrg, name: e.target.value})}
                              placeholder="Nombre"
                              className="bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                            <select
                              value={editingOrg.type}
                              onChange={e => setEditingOrg({...editingOrg, type: e.target.value})}
                              className="bg-white text-slate-850 rounded px-2 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            >
                              <option value="corporate">Corporativo</option>
                              <option value="government">Gubernamental</option>
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input 
                              type="text" 
                              value={editingOrg.ruc}
                              onChange={e => setEditingOrg({...editingOrg, ruc: e.target.value})}
                              placeholder="RUC"
                              className="bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                            <select
                              value={editingOrg.status}
                              onChange={e => setEditingOrg({...editingOrg, status: e.target.value})}
                              className="bg-white text-slate-850 rounded px-2 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            >
                              <option value="active">Activo</option>
                              <option value="inactive">Inactivo</option>
                              <option value="suspended">Suspendido</option>
                            </select>
                          </div>
                          <div className="border-t border-slate-100 pt-2 space-y-2">
                            <input 
                              type="text" 
                              value={editingOrg.contactName}
                              onChange={e => setEditingOrg({...editingOrg, contactName: e.target.value})}
                              placeholder="Nombre Contacto"
                              className="w-full bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                            <input 
                              type="text" 
                              value={editingOrg.contactPhone}
                              onChange={e => setEditingOrg({...editingOrg, contactPhone: e.target.value})}
                              placeholder="Teléfono Contacto"
                              className="w-full bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                            <input 
                              type="email" 
                              value={editingOrg.contactEmail}
                              onChange={e => setEditingOrg({...editingOrg, contactEmail: e.target.value})}
                              placeholder="Email Contacto"
                              className="w-full bg-white text-slate-800 rounded px-2.5 py-1.5 text-xs border border-slate-250 focus:outline-none"
                            />
                          </div>
                          <div className="flex justify-end gap-2 text-xs">
                            <button 
                              type="button" 
                              onClick={() => handleUpdateOrg(o.id, editingOrg.name, editingOrg.type, editingOrg.ruc, editingOrg.contactName, editingOrg.contactEmail, editingOrg.contactPhone, editingOrg.status)}
                              className="bg-primary-600 hover:bg-primary-550 text-white px-3 py-1.5 rounded font-semibold"
                            >
                              Guardar
                            </button>
                            <button 
                              type="button" 
                              onClick={() => setEditingOrg(null)}
                              className="bg-slate-200 hover:bg-slate-305 text-slate-700 px-3 py-1.5 rounded font-semibold"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-start gap-4">
                          <div>
                            <h4 className="text-sm font-bold text-slate-800">{o.name}</h4>
                            <p className="text-xs text-slate-500 font-mono mt-0.5">Slug: {o.slug} • Tipo: {o.type}</p>
                            {o.ruc && <p className="text-xs text-slate-600 mt-1">RUC: {o.ruc}</p>}
                            {o.contact_name && <p className="text-[11px] text-slate-500 mt-1">Contacto: {o.contact_name} ({o.contact_phone || 'S/T'})</p>}
                            <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded font-semibold mt-2 ${o.status === 'active' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                              {o.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button 
                              onClick={() => setEditingOrg({ id: o.id, name: o.name, type: o.type, ruc: o.ruc || '', contactName: o.contact_name || '', contactEmail: o.contact_email || '', contactPhone: o.contact_phone || '', status: o.status })}
                              className="text-primary-600 hover:text-primary-850 hover:bg-primary-50 p-1.5 rounded-lg transition-colors"
                              title="Editar Organización"
                            >
                              <Settings size={16} />
                            </button>
                            <button 
                              onClick={() => handleDeleteOrg(o.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                              title="Eliminar Organización"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Active Organization Memberships */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-6">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Users size={20} className="text-primary-500" />
                  Accesos y Miembros
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Mapeado para la Organización Activa: <strong className="text-slate-800">{organizations.find(o => o.id === orgId)?.name || 'Cargando...'}</strong>
                </p>
              </div>

              {/* Form to Assign user access */}
              <form onSubmit={handleAddMember} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4 shadow-sm">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Otorgar Nuevo Acceso</p>
                
                {/* Search bar inside Grant Access form */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-slate-550">1. Filtrar Usuarios por Nombre o Correo</label>
                  <div className="flex items-center gap-2 bg-white border border-slate-250 px-3 py-1.5 rounded-lg">
                    <Search size={14} className="text-slate-400" />
                    <input 
                      type="text" 
                      placeholder="Escribe para buscar..." 
                      value={userSearchQuery}
                      onChange={e => setUserSearchQuery(e.target.value)}
                      className="bg-transparent text-xs text-slate-800 w-full focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">2. Seleccionar Usuario</label>
                    <select
                      value={newMember.userId}
                      onChange={e => setNewMember({...newMember, userId: e.target.value})}
                      className="bg-white text-slate-850 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                      required
                    >
                      <option value="">-- Seleccionar ({allUsers.filter(u => u.username.toLowerCase().includes(userSearchQuery.toLowerCase()) || (u.email && u.email.toLowerCase().includes(userSearchQuery.toLowerCase()))).length}) --</option>
                      {allUsers.filter(u => 
                        u.username.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                        (u.email && u.email.toLowerCase().includes(userSearchQuery.toLowerCase()))
                      ).map(u => (
                        <option key={u.id} value={u.id}>
                          {u.username} ({u.email || 'sin correo'})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">3. Rol Asignado</label>
                    <select
                      value={newMember.role}
                      onChange={e => setNewMember({...newMember, role: e.target.value})}
                      className="bg-white text-slate-850 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500"
                    >
                      <option value="owner">Owner (Propietario)</option>
                      <option value="org_admin">Org Admin (Administrador)</option>
                      <option value="member">Member (Colaborador)</option>
                    </select>
                  </div>
                </div>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-slate-200 disabled:text-slate-400 text-white py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1 shadow-sm"
                >
                  <Plus size={16} /> Otorgar Acceso
                </button>
              </form>

              {/* Members search & list */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-xs font-bold text-slate-400 uppercase">Miembros Activos</p>
                  <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200 text-xs w-48">
                    <Search size={12} className="text-slate-400" />
                    <input 
                      type="text" 
                      placeholder="Filtrar miembros..." 
                      value={memberSearchQuery}
                      onChange={e => setMemberSearchQuery(e.target.value)}
                      className="bg-transparent text-[11px] text-slate-800 focus:outline-none w-full"
                    />
                  </div>
                </div>

                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {members.filter(m => {
                    const u = allUsers.find(user => user.id === m.user_id);
                    if (!u) return false;
                    return u.username.toLowerCase().includes(memberSearchQuery.toLowerCase()) || 
                           (u.email && u.email.toLowerCase().includes(memberSearchQuery.toLowerCase())) ||
                           m.role.toLowerCase().includes(memberSearchQuery.toLowerCase());
                  }).length === 0 ? (
                    <p className="text-sm text-slate-500 py-4 text-center">No se encontraron miembros.</p>
                  ) : (
                    members.filter(m => {
                      const u = allUsers.find(user => user.id === m.user_id);
                      if (!u) return false;
                      return u.username.toLowerCase().includes(memberSearchQuery.toLowerCase()) || 
                             (u.email && u.email.toLowerCase().includes(memberSearchQuery.toLowerCase())) ||
                             m.role.toLowerCase().includes(memberSearchQuery.toLowerCase());
                    }).map(m => {
                      const matchedUser = allUsers.find(u => u.id === m.user_id);
                      return (
                        <div key={m.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex justify-between items-center hover:border-slate-300 transition-colors">
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-800">
                              {matchedUser ? matchedUser.username : `Usuario ID: ${m.user_id}`}
                            </p>
                            <p className="text-xs text-slate-650 mt-0.5">
                              {matchedUser?.email || 'Sin correo registrado'}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2 shrink-0">
                            {editingMember?.id === m.id ? (
                              <div className="flex items-center gap-1">
                                <select 
                                  value={editingMember.role}
                                  onChange={e => setEditingMember({...editingMember, role: e.target.value})}
                                  className="bg-white border text-xs px-1.5 py-1 rounded focus:outline-none"
                                >
                                  <option value="owner">Owner</option>
                                  <option value="org_admin">Org Admin</option>
                                  <option value="member">Member</option>
                                </select>
                                <button 
                                  onClick={() => handleUpdateMember(m.id, editingMember.role)}
                                  className="bg-primary-600 hover:bg-primary-750 text-white text-[10px] px-2 py-1 rounded"
                                >
                                  OK
                                </button>
                                <button 
                                  onClick={() => setEditingMember(null)}
                                  className="bg-slate-200 text-slate-700 text-[10px] px-2 py-1 rounded"
                                >
                                  X
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className="font-semibold uppercase text-primary-600 text-[10px] bg-primary-50 px-2 py-0.5 rounded border border-primary-100">
                                  {m.role}
                                </span>
                                <button 
                                  onClick={() => setEditingMember({ id: m.id, role: m.role })}
                                  className="text-primary-500 hover:text-primary-750 hover:bg-primary-50 p-1.5 rounded-lg transition-colors"
                                  title="Editar Rol"
                                >
                                  <Settings size={14} />
                                </button>
                                <button 
                                  onClick={() => handleDeleteMember(m.id)}
                                  disabled={loading}
                                  className="text-red-500 hover:text-red-750 hover:bg-red-50 p-1.5 rounded-lg transition-colors disabled:opacity-50"
                                  title="Revocar Acceso"
                                >
                                  <Trash2 size={15} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Members & Accesses Content (Organization Admins / Owners / Super Admin) */}
      {activeSubTab === 'members' && (
        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <Users className="text-primary-500" size={22} />
              Miembros y Accesos de la Organización
            </h3>
            <p className="text-sm text-slate-650 mt-1">
              Administra los usuarios vinculados a esta organización y sus roles de acceso.
            </p>
          </div>

          {!hasMemberMgmtAccess ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-3">
              <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
              <div>
                <h4 className="text-sm font-bold text-amber-900">Acceso Restringido</h4>
                <p className="text-xs text-amber-700 leading-relaxed mt-1">
                  Solo los administradores o propietarios de la organización (Owner / Org Admin) tienen permisos para gestionar los accesos y miembros.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form to Assign user access */}
              <div className="lg:col-span-1 bg-slate-50 p-5 rounded-xl border border-slate-200 h-fit space-y-4">
                <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Otorgar Acceso a Usuario</h4>
                <form onSubmit={handleAddMember} className="space-y-4">
                  {/* Filter Search inside grant access form */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-slate-500">1. Buscar Usuario</label>
                    <div className="flex items-center gap-2 bg-white border border-slate-250 px-2.5 py-1.5 rounded-lg">
                      <Search size={14} className="text-slate-400" />
                      <input 
                        type="text" 
                        placeholder="Nombre o email..." 
                        value={userSearchQuery}
                        onChange={e => setUserSearchQuery(e.target.value)}
                        className="bg-transparent text-xs text-slate-800 w-full focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase">2. Seleccionar Usuario</label>
                    <select
                      value={newMember.userId}
                      onChange={e => setNewMember({...newMember, userId: e.target.value})}
                      className="bg-white text-slate-850 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500 w-full [&>option]:text-slate-800"
                      required
                    >
                      <option value="">-- Seleccionar ({allUsers.filter(u => u.username.toLowerCase().includes(userSearchQuery.toLowerCase()) || (u.email && u.email.toLowerCase().includes(userSearchQuery.toLowerCase()))).length}) --</option>
                      {allUsers.filter(u => 
                        u.username.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                        (u.email && u.email.toLowerCase().includes(userSearchQuery.toLowerCase()))
                      ).map(u => (
                        <option key={u.id} value={u.id}>
                          {u.username} ({u.email || 'sin correo'})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase">3. Rol Asignado</label>
                    <select
                      value={newMember.role}
                      onChange={e => setNewMember({...newMember, role: e.target.value})}
                      className="bg-white text-slate-855 rounded-lg px-3 py-2 text-sm border border-slate-250 focus:outline-none focus:border-primary-500 w-full"
                    >
                      <option value="owner">Owner (Propietario)</option>
                      <option value="org_admin">Org Admin (Administrador)</option>
                      <option value="member">Member (Colaborador)</option>
                    </select>
                  </div>
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-slate-200 disabled:text-slate-400 text-white py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1 shadow-sm"
                  >
                    <Plus size={16} /> Otorgar Acceso
                  </button>
                </form>
              </div>

              {/* Members list */}
              <div className="lg:col-span-2 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Miembros Activos</h4>
                  
                  {/* Search inside members list */}
                  <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200 text-xs w-48 shadow-sm">
                    <Search size={12} className="text-slate-400" />
                    <input 
                      type="text" 
                      placeholder="Filtrar miembros..." 
                      value={memberSearchQuery}
                      onChange={e => setMemberSearchQuery(e.target.value)}
                      className="bg-transparent text-[11px] text-slate-850 focus:outline-none w-full"
                    />
                  </div>
                </div>

                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {members.filter(m => {
                    const u = allUsers.find(user => user.id === m.user_id);
                    if (!u) return false;
                    return u.username.toLowerCase().includes(memberSearchQuery.toLowerCase()) || 
                           (u.email && u.email.toLowerCase().includes(memberSearchQuery.toLowerCase())) ||
                           m.role.toLowerCase().includes(memberSearchQuery.toLowerCase());
                  }).length === 0 ? (
                    <p className="text-sm text-slate-505 py-4 text-center">No se encontraron miembros.</p>
                  ) : (
                    members.filter(m => {
                      const u = allUsers.find(user => user.id === m.user_id);
                      if (!u) return false;
                      return u.username.toLowerCase().includes(memberSearchQuery.toLowerCase()) || 
                             (u.email && u.email.toLowerCase().includes(memberSearchQuery.toLowerCase())) ||
                             m.role.toLowerCase().includes(memberSearchQuery.toLowerCase());
                    }).map(m => {
                      const matchedUser = allUsers.find(u => u.id === m.user_id);
                      return (
                        <div key={m.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex justify-between items-center hover:border-slate-300 transition-colors">
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-850">
                              {matchedUser ? matchedUser.username : `Usuario ID: ${m.user_id}`}
                            </p>
                            <p className="text-xs text-slate-605 mt-0.5">
                              {matchedUser?.email || 'Sin correo registrado'}
                            </p>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {editingMember?.id === m.id ? (
                              <div className="flex items-center gap-1">
                                <select 
                                  value={editingMember.role}
                                  onChange={e => setEditingMember({...editingMember, role: e.target.value})}
                                  className="bg-white border text-xs px-1.5 py-1 rounded focus:outline-none"
                                >
                                  <option value="owner">Owner</option>
                                  <option value="org_admin">Org Admin</option>
                                  <option value="member">Member</option>
                                </select>
                                <button 
                                  onClick={() => handleUpdateMember(m.id, editingMember.role)}
                                  className="bg-primary-600 hover:bg-primary-750 text-white text-[10px] px-2 py-1 rounded"
                                >
                                  OK
                                </button>
                                <button 
                                  onClick={() => setEditingMember(null)}
                                  className="bg-slate-200 text-slate-700 text-[10px] px-2 py-1 rounded"
                                >
                                  X
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className="font-semibold uppercase text-primary-600 text-[10px] bg-primary-50 px-2 py-0.5 rounded-full border border-primary-100">
                                  {m.role}
                                </span>
                                <button 
                                  onClick={() => setEditingMember({ id: m.id, role: m.role })}
                                  className="text-primary-500 hover:text-primary-750 hover:bg-primary-50 p-1.5 rounded-lg transition-colors"
                                  title="Editar Rol"
                                >
                                  <Settings size={14} />
                                </button>
                                <button 
                                  onClick={() => handleDeleteMember(m.id)}
                                  disabled={loading}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-xl transition-colors disabled:opacity-50"
                                  title="Revocar Acceso"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
